import { useEffect, useState } from 'react';
import SectionHeader from 'components/SectionHeader';
import StandardOrganizationNodeSelector from 'components/SelectComponents/StandardOrganizationNodeSelector';
import AppPermissionsTable from 'components/Users/AppPermissionsTable';
import UserLanguageSelector from 'components/Users/LanguageSelector';
import OrganizationNodesAndRolesTable from 'components/Users/OrganizationNodesAndRolesTable';
import ScopesTable from 'components/Users/ScopesTable';
import UserHistoryAndLogin from 'components/Users/UserHistoryAndLogin';
import { useObjectIdRouteParam } from 'hooks/useObjectIdRouteParam';
import intl from 'i18n/intl';
import api from 'services/api.service';
import { userSelector } from 'slices/apiKeys.slice';
import { useAppSelector } from 'slices/store';
import { getLocalDisplayFormatFromTimestamp } from 'utils/timeConverter';
import { EUserVisibility, type TM2MUser } from '@timeedit/types';
import { Icon } from '@timeedit/ui-components';
import {
  Button,
  Collapse,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
} from '@timeedit/ui-components/antd';

type Props = {
  isReadOnly?: boolean;
  user: TM2MUser;
  setM2MUser: (user: TM2MUser) => void;
};

const language = intl.messages;

const ApiKeyDetailPage = ({ isReadOnly = false, user, setM2MUser }: Props) => {
  const [form] = Form.useForm();

  /**
   * SELECTORS
   */
  const userId = useObjectIdRouteParam();
  const storeUser = useAppSelector(userSelector(userId));
  const [generatedKeyString, setGeneratedKeyString] = useState<string>('');

  useEffect(() => {
    if (userId !== user.id) {
      setM2MUser(storeUser);
      onResetGeneratedKey();
    }
  }, [userId]);

  // Handle changes
  const onChange = (prop: string, value: any) => {
    setM2MUser({ ...user, [prop]: value });
  };

  // Handle generate key
  const onGenerateKey = () => {
    const gen = async () => {
      const response = await api.post({
        endpoint: `/api-keys/${user.id}/generate`,
      });
      if (response?.key) setGeneratedKeyString(response.key);
    };
    gen();
  };

  // Resets local store generated key
  const onResetGeneratedKey = () => {
    setGeneratedKeyString('');
  };

  // Copy current generated key to clipboard (ie. ctrl + c)
  const onCopyKeyToClipboard = () => {
    const copy = async () => {
      await navigator.clipboard.writeText(generatedKeyString);
    };
    copy();
  };

  const containAllRequiredData = () => {
    return !!user.name;
  };

  return (
    <>
      <SectionHeader
        listChoiceHeading={user.name}
        displayProp={false}
        history={user.history}
        isDisabled={!containAllRequiredData()}
      />
      <Form layout="vertical" form={form}>
        <div>
          <UserHistoryAndLogin history={user.history} type="change" />
          <p className="small-heading-text">
            {`${language.created}: ${
              user.history.length
                ? getLocalDisplayFormatFromTimestamp(user.createdAt, 'sec')
                : 'N/A'
            }`}
          </p>
        </div>
        <Collapse defaultActiveKey={['1']} ghost style={{ fontSize: 18 }}>
          <Collapse.Panel header={`${language.general_settings}`} key="1">
            <Form.Item label={`${language.name}`}>
              <Input
                size="small"
                value={user.name}
                onChange={(e) => onChange('name', e.target.value)}
                placeholder="name"
                disabled={isReadOnly}
              />
            </Form.Item>
            <Form.Item label={`${language.contact_email}`}>
              <Input
                size="small"
                value={user.contactEmail || ''}
                onChange={(e) => onChange('contactEmail', e.target.value)}
                placeholder="Contact email"
                disabled={isReadOnly}
              />
            </Form.Item>
            <Form.Item label={`${language.description}`}>
              <Input.TextArea
                size="small"
                rows={5}
                value={user.description || undefined}
                onChange={(e) => onChange('description', e.target.value)}
                placeholder="Description"
                disabled={isReadOnly}
              />
            </Form.Item>
            <Form.Item label={`${language.language}`}>
              <UserLanguageSelector
                disabled={isReadOnly}
                value={user.language}
                onChange={(language) => onChange('language', language)}
                placeholder="Language"
              />
            </Form.Item>
            <Form.Item label={`${language.visibility}`}>
              <Select
                size="small"
                showSearch
                filterOption={(input: any, option: any) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >=
                  0
                }
                value={language[user.visibility]}
                onChange={(e) => onChange('visibility', e)}
                placeholder="Visibility"
                disabled={isReadOnly}
              >
                {Object.entries(EUserVisibility).map(([key, value]) => (
                  <Select.Option value={value} key={key}>
                    {`${language[value]}`}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Collapse.Panel>
        </Collapse>
        <Collapse defaultActiveKey={['1']} ghost style={{ fontSize: 18 }}>
          <Collapse.Panel header="Permissions" key="1">
            <Form.Item label={'Scopes'}>
              <ScopesTable
                disabled={isReadOnly}
                userScopeValues={user.scopes || []}
                onChange={(newScopes) => onChange('scopes', newScopes)}
              />
            </Form.Item>
            <Form.Item label="App permissions">
              <AppPermissionsTable
                disabled={isReadOnly}
                userAppPermissionValues={user.appPermissions || []}
                onChange={(newAppPermissions) =>
                  onChange('appPermissions', newAppPermissions)
                }
              />
            </Form.Item>
          </Collapse.Panel>
        </Collapse>
        <Collapse defaultActiveKey={['1']} ghost style={{ fontSize: 18 }}>
          <Collapse.Panel header="Organizations, roles" key="1">
            <Form.Item label="Standard organization node">
              <StandardOrganizationNodeSelector
                selectedStandardOrganizationId={user.standardOrganization}
                onChange={(value) => onChange('standardOrganization', value)}
              />
            </Form.Item>
            <Form.Item label="Organization nodes and roles">
              <OrganizationNodesAndRolesTable
                title="Organization nodes and roles"
                tooltip="Organization nodes and roles"
                disabled={isReadOnly}
                organizationNodesAndRoles={user.organizationsAndRoles}
                onChange={(newValue) =>
                  onChange('organizationsAndRoles', newValue)
                }
              />
            </Form.Item>
          </Collapse.Panel>
        </Collapse>
        <Collapse defaultActiveKey={['1']} ghost style={{ fontSize: 18 }}>
          <Collapse.Panel header="Api Key Creation" key="1">
            <Popconfirm
              title="Are you sure you want to proceed?"
              description="This will invalidate any previously generated token and break all integrations using that token."
              onConfirm={onGenerateKey}
            >
              <Button
                size="small"
                type="default"
                style={{ marginBottom: '20px' }}
              >
                {'Generate Key'}
              </Button>
            </Popconfirm>
          </Collapse.Panel>
        </Collapse>
      </Form>
      <Modal
        open={generatedKeyString !== ''}
        maskClosable={false}
        onCancel={onResetGeneratedKey}
        onOk={onResetGeneratedKey}
        footer={[
          <Button key="Ok" type="primary" onClick={onResetGeneratedKey}>
            OK
          </Button>,
        ]}
      >
        <div
          style={{
            marginTop: '20px',
            textAlign: 'center',
            border: '1px solid #d9d9d9',
          }}
        >
          <p
            style={{
              display: 'inline',
            }}
          >
            {generatedKeyString}
          </p>
          <Button
            onClick={onCopyKeyToClipboard}
            size="small"
            className={`list-item-action-button copy}`}
            icon={<Icon id="CopyOutlined" alt="Copy" />}
          />
        </div>
        <p
          className="small-heading-text"
          style={{ marginBottom: 0, marginTop: '20px' }}
        >
          <b>Note</b>:
          <ul>
            <li>{`${language.help_api_key_storage}`}</li>
            <li>{`${language.help_api_key_regenerate}`}</li>
            <li>{`${language.help_api_key_properties}`}</li>
          </ul>
        </p>
      </Modal>
    </>
  );
};

export default ApiKeyDetailPage;
